import dayjs from "dayjs";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { ptBR } from "@mui/x-data-grid/locales";

import { IGovernanceBeneficiary } from "./Governance.types";

type Props = {
  data: IGovernanceBeneficiary[];
};

export function GovernanceUploadTable({ data }: Props) {
  const formatMovementType = (type: IGovernanceBeneficiary["movementType"]) => {
    return type === "INCLUSAO" ? "Inclusão" : "Exclusão";
  };

  /** As datas vêm do JSONB como string ISO, não como `Date`. */
  const formatDate = (value?: string) => (value ? dayjs(value).format("DD/MM/YYYY") : "-");

  const rows = data.map((beneficiary) => ({
    /** `linha` é o número da linha na planilha, único dentro de um upload. */
    id: beneficiary.linha,
    /** CPF e CNPJ já vêm mascarados: a validação exige o formato e não desmascara. */
    cpfTitular: beneficiary.CPF_TITULAR || "-",
    nomeTitular: beneficiary.NOME_TITULAR || "-",
    cpfSegurado: beneficiary.CPF_SEGURADO || "-",
    nomeSegurado: beneficiary.NOME_SEGURADO || "-",
    movimentacao: formatMovementType(beneficiary.movementType),
    vinculo: beneficiary.vinculo,
    grauParentesco: beneficiary.GRAU_PARENTESCO || "-",
    cnpjPj: beneficiary.CNPJ_PESSOA_JURIDICA || "-",
    matricula: beneficiary.MATRICULA || "-",
    numeroContrato: beneficiary.NUMERO_CONTRATO || "-",
    codigoPlano: beneficiary.CODIGO_PLANO || "-",
    statusEmpresa: beneficiary.STATUS_BENEFICIARIO_EMPRESA || "-",
    statusOperadora: beneficiary.STATUS_OPERADORA || "-",
    dataNascimento: formatDate(beneficiary.DATA_NASCIMENTO),
    dataAdmissao: formatDate(beneficiary.DATA_ADMISSAO),
    dataDemissao: formatDate(beneficiary.DATA_DEMISSAO),
    vigenciaInicial: formatDate(beneficiary.DATA_VIGENCIA_INICIAL),
    vigenciaFinal: formatDate(beneficiary.DATA_VIGENCIA_FINAL),
  }));

  const columns: GridColDef[] = [
    {
      field: "cpfTitular",
      headerName: "CPF do Titular",
      width: 160,
      sortable: true,
    },
    {
      field: "nomeTitular",
      headerName: "Nome do Titular",
      width: 260,
      sortable: true,
    },
    {
      field: "cpfSegurado",
      headerName: "CPF do Beneficiário",
      width: 160,
      sortable: true,
    },
    {
      field: "nomeSegurado",
      headerName: "Nome do Beneficiário",
      width: 260,
      sortable: true,
    },
    {
      field: "movimentacao",
      headerName: "Movimentação",
      width: 140,
      sortable: true,
    },
    {
      field: "vinculo",
      headerName: "Vínculo",
      width: 100,
      sortable: true,
    },
    {
      field: "grauParentesco",
      headerName: "Grau de Parentesco",
      width: 260,
      sortable: true,
    },
    {
      field: "cnpjPj",
      headerName: "CNPJ do PJ",
      width: 180,
      sortable: true,
    },
    {
      field: "matricula",
      headerName: "Matrícula",
      width: 130,
      sortable: true,
    },
    {
      field: "numeroContrato",
      headerName: "Nº do Contrato",
      width: 160,
      sortable: true,
    },
    {
      field: "codigoPlano",
      headerName: "Código do Plano",
      width: 160,
      sortable: true,
    },
    {
      field: "statusEmpresa",
      headerName: "Status na Empresa",
      width: 170,
      sortable: true,
    },
    {
      field: "statusOperadora",
      headerName: "Status na Operadora",
      width: 210,
      sortable: true,
    },
    {
      field: "dataNascimento",
      headerName: "Nascimento",
      width: 130,
      sortable: true,
    },
    {
      field: "dataAdmissao",
      headerName: "Admissão",
      width: 130,
      sortable: true,
    },
    {
      field: "dataDemissao",
      headerName: "Demissão",
      width: 130,
      sortable: true,
    },
    {
      field: "vigenciaInicial",
      headerName: "Início da Vigência",
      width: 170,
      sortable: true,
    },
    {
      field: "vigenciaFinal",
      headerName: "Fim da Vigência",
      width: 160,
      sortable: true,
    },
  ];

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      initialState={{
        pagination: {
          paginationModel: { page: 0, pageSize: 7 },
        },
      }}
      pageSizeOptions={[7, 10, 25, 50]}
      disableRowSelectionOnClick
      localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
      style={{ backgroundColor: "white", minHeight: 400 }}
      sx={{
        "& .MuiDataGrid-cell": {
          fontSize: "0.875rem",
        },
        "& .MuiDataGrid-columnHeader": {
          fontSize: "0.875rem",
          fontWeight: 600,
        },
      }}
    />
  );
}
